<template>
  <header
    id="header"
    class="sticky top-0 z-10 transition-all duration-200"
    :class="{
      'max-md:-translate-y-full': shouldHideHeader,
      'md:-translate-y-full': shouldHideHeaderOnMd,
    }"
  >
    <div class="flex w-full">
      <div
        ref="header"
        class="bg-static-default-low border-static-default-low relative z-[1] w-full border-b-1 transition-all duration-200"
      >
        <ErrorBoundary
          v-if="bannerEnabled && showPromotionalBanner"
          :owners="['bot-squad-content-front']"
        >
          <PromotionalBanner
            :class="{
              hidden: !displayPromotionalBannerOnPage,
              'hidden md:block':
                isSearchBarActive && displayPromotionalBannerOnPage,
            }"
            data-test="promotional-banner-wrapper"
          />
        </ErrorBoundary>
        <Banner
          class="relative z-[2] hidden md:flex"
          :class="[
            displayPromotionalBannerOnPage
              ? 'bg-static-default-low'
              : 'bg-surface-default-hi',
          ]"
        />
        <Main />
        <NavMenu class="hidden md:block" />
      </div>
    </div>
    <!-- we only render the NavBurgerMenu after hydration is completed to avoid unnecessary execution initially -->
    <NavBurgerMenu v-if="hydrationCompleted" v-show="isSidebarOpened" />
    <ClientOnly>
      <CountrySwitchRedirection
        v-if="displayCountrySwitchOnPage"
        ref="countrySwitchBannerRef"
      />
    </ClientOnly>
  </header>
</template>

<script setup lang="ts">
import { useRoute, useRuntimeConfig } from '#imports'
import { computed } from 'vue'

import { useIsHydrationCompleted } from '@backmarket/nuxt-layer-base/composables/useIsHydrationComplete'
import PromotionalBanner from '@backmarket/nuxt-layer-cms/PromotionalBanner.vue'
import { useAddToCartPopover } from '@backmarket/nuxt-layer-recommendation/useAddToCartPopover'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import ErrorBoundary from '@backmarket/nuxt-module-logger/components/ErrorBoundary.vue'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import { CHECKOUT as CHECKOUT_ROUTES } from '~/scopes/checkout/routes-names'
import { HOME } from '~/scopes/home/route-names'
import { PRODUCT_ROUTES } from '~/scopes/product-core/route-names'
import { ROUTES as PRODUCT_LIST_ROUTES } from '~/scopes/product-list/route-names'
import { useToggleSearchBar } from '~/scopes/search-bar/composables/useToggleSearchBar'

import { useActiveMenu } from '../../composables/useActiveMenu'
import { useBurgerMenuState } from '../../composables/useBurgerMenuState'
import { useHideOnScroll } from '../../composables/useHideOnScroll'
import CountrySwitchRedirection from '../CountrySwitch/CountrySwitchRedirection.vue'
import NavBurgerMenu from '../NavBurgerMenu/NavBurgerMenu.vue'
import NavMenu from '../NavMenu/NavMenu.vue'

import Banner from './components/Banner.vue'
import Main from './components/Main.vue'

const hydrationCompleted = useIsHydrationCompleted()

const { hidden: isHeaderHidden } = useHideOnScroll()
const { isOpen: isSidebarOpened } = useBurgerMenuState()
const { isMenuActive } = useActiveMenu()
const { isSearchBarActive } = useToggleSearchBar()
const { isVisible: isAddToCartPopoverVisible } = useAddToCartPopover()
const experiments = useExperiments()
const route = useRoute()
const country = useMarketplace().market.countryCode
const {
  public: { FF_CMS_ENABLE_PROMOTIONAL_BANNER_COUNTRIES },
} = useRuntimeConfig()

const bannerEnabled =
  FF_CMS_ENABLE_PROMOTIONAL_BANNER_COUNTRIES?.includes(country)

const showPromotionalBanner =
  experiments['experiment.skinnyOrPromotionalBannerV3'] === 'promotionalBanner'

const hasCountrySwitchRedirection =
  experiments['experiment.countrySwitchRedirection'] === 'withRedirection'

const displayPromotionalBannerOnPage = computed(() => {
  if (!showPromotionalBanner || !route.name || !bannerEnabled) return false

  const displayBannerRoutes = [
    HOME,
    PRODUCT_LIST_ROUTES.PRODUCT_LIST,
    PRODUCT_ROUTES.VARIANT_PRODUCT_PAGE,
    PRODUCT_ROUTES.MASTER_PRODUCT_PAGE,
    PRODUCT_LIST_ROUTES.SEARCH_RESULTS,
    PRODUCT_LIST_ROUTES.SELLER_PRODUCT_LIST,
  ]

  return displayBannerRoutes.includes(route.name.toString())
})

const displayCountrySwitchOnPage = computed(() => {
  if (!route.name || !hasCountrySwitchRedirection) return false

  const excludedRoutes = [CHECKOUT_ROUTES.PAYMENT_RESULT]

  return !excludedRoutes.includes(route.name.toString())
})

const shouldHideHeader = computed(
  () =>
    isHeaderHidden.value &&
    !isSearchBarActive.value &&
    !isSidebarOpened.value &&
    !isMenuActive.value,
)

const shouldHideHeaderOnMd = computed(
  () => shouldHideHeader.value && !isAddToCartPopoverVisible.value,
)
</script>
